import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Phone, Star, Camera, CheckCircle, Clock, Award,
  MessageCircle, ChevronRight, Shield, ArrowRight, Calendar,
  Search, Eye, Globe, Target, TrendingUp, X
} from "lucide-react";
import { ContactSection, SectionDivider } from "@/Layout";
import { WA_LINK, CALENDLY, GMAPS_REVIEWS } from "@/Layout";
import { WhatsAppBookingForm } from "@/components/WhatsAppBookingForm";
import { CrossSellingGift, CrossSellingProducts } from "@/components/CrossSelling";
import { TShirtBundle } from "@/components/TShirtBundle";

const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1">
    {[1,2,3,4,5].map(s => (
      <Star key={s} className={`w-4 h-4 ${s <= Math.floor(rating) ? 'text-[#FFA41C] fill-[#FFA41C]' : s <= rating ? 'text-[#FFA41C] fill-[#FFA41C] opacity-50' : 'text-gray-300'}`} />
    ))}
    <span className="text-sm text-blue-600 ml-1">({count})</span>
  </div>
);

// Plus Pricing Card
const PlusPricingCard = () => (
  <section className="py-16 bg-[#0c1220]">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 transition-colors" data-testid="back-home">
        <ChevronRight className="w-4 h-4 rotate-180" /> Torna alle 4 porte
      </Link>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="w-4 h-4 rounded-full bg-[#FBBC04]"></span>
          <Badge className="bg-yellow-100 text-[#FBBC04] border-yellow-300">IL PIU VENDUTO</Badge>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Plus — <span className="text-[#FBBC04]">Il Preferito</span>
        </h1>
        <p className="text-gray-500">Assistenza concreta: sospensione, recensioni, consulenza</p>
      </div>
      <div className="bg-white/5 rounded-2xl border-2 border-red-500/30 p-6 md:p-8 relative">
        <div className="bg-red-500 text-white text-center py-2 rounded-t-xl absolute -top-px left-0 right-0 text-sm font-bold" style={{borderRadius: '14px 14px 0 0'}}>IL PIU VENDUTO</div>
        <div className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <StarRating rating={4.8} count={47} />
          </div>
          <div className="mt-3 mb-2">
            <div className="flex items-baseline gap-3">
              <span className="text-2xl text-gray-500 line-through">€399</span>
              <div className="flex items-baseline">
                <span className="text-sm text-[#FBBC04] mr-1">€</span>
                <span className="text-5xl font-bold text-[#FBBC04] tracking-tight">299</span>
              </div>
            </div>
            <p className="text-xs text-gray-500">una tantum · oppure €99,66 x3 rate PayPal · TAEG 0%</p>
            <p className="text-xs text-[#FBBC04] font-bold mt-1">Solo per i primi 10 — il piu venduto!</p>
          </div>
          <div className="bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2 mb-4">
            <p className="text-xs text-red-700 font-medium"><Clock className="w-3 h-3 inline mr-1" />Valido entro <strong>7 giorni</strong> dal primo contatto</p>
          </div>
          <ul className="space-y-2 mb-6">
            {["Tutto di Start incluso", "Recupero scheda sospesa", "Risposta recensioni", "5 foto professionali", "3 foto sferiche/immersive in REGALO", "Consulenza strategica 1h", "Risposta recensioni 1 mese", "Report mensile"].map((f, i) => (
              <li key={i} className="flex items-start gap-2 border border-black/40 rounded-lg px-3 py-2 bg-black/10"><CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${f.includes('REGALO') ? 'text-[#ff6b35]' : 'text-[#FBBC04]'}`} /><span className={f.includes('REGALO') ? 'text-[#ff6b35] font-bold' : 'text-gray-300'}>{f}</span></li>
            ))}
          </ul>
          <button className="w-full bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-bold py-4 rounded-full border border-[#FCD200] shadow-md transition-all active:scale-95 text-lg relative overflow-hidden group"
            onClick={() => window.open(`https://www.paypal.me/Riccarossi/299`, '_blank')} data-testid="plus-pkg-btn">
            <span className="relative z-10 inline-flex items-center justify-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              <span className="cta-wave-text">Paga con PayPal — €299</span>
            </span>
            <span className="cta-talpa-shine"></span>
          </button>
          <div className="flex items-center justify-center gap-2 mt-2">
            <svg className="h-4 w-auto" viewBox="0 0 101 32" fill="#003087"><path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/><path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/></svg>
            <span className="text-xs text-gray-500">oppure <strong className="text-[#003087]">€133 x 3 rate</strong> · TAEG 0%</span>
          </div>
          <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-center">
            <p className="text-xs text-teal-800 font-medium">Vuoi anche il Virtual Tour? Scegli <strong>Virtual Tour 360</strong> a €499 (Start incluso!)</p>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-4">
        <Link to="/start" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">← Start €99</Link>
        <Link to="/rooftop" className="text-sm text-gray-500 hover:text-amber-600 transition-colors">Rooftop €599 →</Link>
      </div>
    </div>
  </section>
);

// Suspension Alert Section
const SuspensionAlertSection = () => (
  <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50 relative overflow-hidden">
    <div className="absolute top-10 right-10 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
            <div className="bg-white/5 px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <span className="text-gray-600 font-medium">Business Profile</span>
              <span className="text-blue-400 font-medium text-lg">Google</span>
            </div>
            <div className="p-6 md:p-8">
              <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-6 leading-tight">
                Your business profile has been <span className="text-red-600 font-normal">suspended</span>
              </h3>
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <p className="text-gray-400 text-sm"><span className="blur-sm">████████</span> <strong>La Tua Azienda</strong></p>
                <p className="text-gray-400 text-sm mt-1">your Business Profile has been suspended due to <span className="text-red-600">policy violation</span>.</p>
              </div>
              <button className="bg-blue-600 text-white px-6 py-2 rounded text-sm font-medium">Appeal</button>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-pulse">ALLERTA</div>
        </div>
        <div>
          <Badge className="bg-red-100 text-red-600 border-red-300 mb-6"><Shield className="w-4 h-4 mr-2" />Hai ricevuto questa email?</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Se ti è arrivata una mail del genere, <span className="text-[#ff6b35]">CONTATTAMI SUBITO!</span>
          </h2>
          <div className="bg-gray-900 text-white p-6 rounded-2xl mb-8 shadow-xl">
            <p className="text-lg leading-relaxed">
              Avere una scheda attività sospesa e <strong className="text-red-400">restare fermi</strong> è come se nel
              <span className="text-yellow-400 font-bold"> 1985</span> te ne stavi con le mani in mano sapendo di
              <strong className="text-red-400"> NON essere sull'elenco telefonico della Sip/Telecom</strong>...
            </p>
          </div>
          <p className="text-xl text-gray-700 mb-8"><strong>Ma dai!</strong> Contattami, svolgo il reclamo, presento il ricorso e... <span className="text-green-600 font-bold text-2xl">fatto!</span></p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-full px-8" onClick={() => window.open(`${WA_LINK}?text=Ciao! Ho ricevuto una mail di sospensione della mia scheda Google!`, '_blank')} data-testid="suspension-whatsapp-btn">
              <MessageCircle className="w-5 h-5 mr-2" /> SOS WhatsApp
            </Button>
            <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-8" onClick={() => window.open(CALENDLY, '_blank')}>
              <Phone className="w-5 h-5 mr-2" /> Chiamata Urgente
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Manifesto Section
const ManifestoSection = () => (
  <section className="py-16 bg-[#0c1220] relative overflow-hidden">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="text-center mb-12">
        <Badge className="bg-blue-100 text-blue-700 border-blue-300 mb-4"><Globe className="w-4 h-4 mr-2" />Il Manifesto</Badge>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">La Missione che Guida <span className="text-[#ff6b35]">Ogni Mio Intervento</span></h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 items-start mb-12">
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video" data-testid="manifesto-video">
          <iframe src="https://www.youtube-nocookie.com/embed/q-V8oipCpxA?rel=0&modestbranding=1" title="Google Business Profile in Italiano - schedabusiness" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"></iframe>
        </div>
        <div>
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-blue-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-white/5 rounded-xl shadow flex items-center justify-center"><span className="text-blue-400 font-bold text-lg">G</span></div>
              <h3 className="text-xl font-bold text-white">La Missione di Google</h3>
            </div>
            <blockquote className="text-gray-300 leading-relaxed mb-6 border-l-4 border-blue-400 pl-4 italic">
              "Organizzare le informazioni a livello mondiale e renderle universalmente accessibili a tutti."
            </blockquote>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0"><Search className="w-4 h-4 text-blue-600" /></div>
                <div><p className="font-bold text-white text-sm" style={{ textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>Organizzare le informazioni</p><p className="text-gray-400 text-sm">Non si limita a indicizzare il web, ma mira a comprendere le conoscenze del mondo.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0"><Globe className="w-4 h-4 text-green-600" /></div>
                <div><p className="font-bold text-white text-sm" style={{ textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>Accessibilità universale</p><p className="text-gray-400 text-sm">Rendere le informazioni reperibili a chiunque, da qualsiasi dispositivo.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center flex-shrink-0"><Target className="w-4 h-4 text-[#ff6b35]" /></div>
                <div><p className="font-bold text-white text-sm" style={{ textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>Utilità concreta</p><p className="text-gray-400 text-sm">Le informazioni devono essere utili per prendere decisioni e trovare <strong>soluzioni</strong>.</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <Card className="bg-gradient-to-br from-[#ff6b35]/5 to-orange-50 border-[#ff6b35]/20 shadow-xl order-2 lg:order-1">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-[#ff6b35] rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">RR</div>
              <div><h3 className="text-lg font-bold text-white">Riccardo Rossi</h3><p className="text-sm text-[#ff6b35] font-medium">Molto più di una Local Guide Livello 8</p></div>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              Sono <strong>molto di più</strong> di una Local Guide livello 8. Le foto a 360° che trovi su Google Maps nella <strong>TUA</strong> città?
              Molto probabilmente, se è una foto a 360°, <strong className="text-[#ff6b35]">l'ho scattata io dal 2017 ad oggi</strong>.
            </p>
            <a href="https://www.google.com/maps/contrib/108784403898325878756/photos/@47.3446894,1.6406076,1208525m/data=!3m2!1e3!4b1!4m3!8m2!3m1!1e1?entry=ttu" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-4 py-2 mb-5 transition-colors group" data-testid="local-guide-link">
              <Eye className="w-4 h-4 text-blue-600" /><span className="text-sm text-blue-700 font-medium group-hover:underline">Guarda le mie foto su Google Maps →</span>
            </a>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-[#ff6b35]/10 text-[#ff6b35] border-[#ff6b35]/20 px-3 py-1"><Camera className="w-3 h-3 mr-1" />Fotografo Certificato</Badge>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-3 py-1"><MapPin className="w-3 h-3 mr-1" />Local Guide Lv.8</Badge>
            </div>
          </CardContent>
        </Card>
        <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video" data-testid="manifesto-video-2">
          <iframe src="https://www.youtube-nocookie.com/embed/SgHZdoQ_nEc?rel=0&modestbranding=1" title="SOS Business Profile" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"></iframe>
        </div>
      </div>
    </div>
  </section>
);

// Reviews Section - Carousel with 9 reviews (3 groups of 3)
const ReviewsSection = () => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(true);

  const reviews = [
    { name: "Gelateria Buekke", text: "Un grande ringraziamento a Riccardo che con la sua disponibilità e competenza ci ha aiutati a valorizzare la nostra attività. Grazie mille!", date: "5 anni fa", avatarColor: "bg-amber-600", initial: "G" },
    { name: "Enoteca il Salotto", text: "Grazie Riccardo per la tua professionalità e simpatia. Non vediamo l'ora di lavorare di nuovo insieme!", date: "5 anni fa", avatarColor: "bg-red-700", initial: "E" },
    { name: "Spazio 847", text: "Abbiamo avuto il piacere di collaborare con Riccardo che ci ha aiutato a far muovere i primi passi su Google con alcune foto a 360 gradi della nostra sala polivalente Spazio 847 a Colle di Val d'Elsa. Qualità e professionalità.", date: "5 anni fa", avatarColor: "bg-blue-600", initial: "S" },
    { name: "Luca Gorini", text: "Complimenti a Riccardo Rossi che ci ha fatto capire l'importanza della visibilità in un momento difficile. Negli anni di attività abbiamo sempre sottovalutato questo aspetto.", date: "5 anni fa", avatarColor: "bg-green-600", initial: "L" },
    { name: "Alessandra Massi", text: "Dopo aver cambiato sede, Riccardo mi ha dato la possibilità di farmi conoscere e riconoscere! Il tutto con gentilezza e professionalità e attraverso foto a 360 gradi che mostrano il mio nuovo negozio! Grazie Riccardo!", date: "5 anni fa", avatarColor: "bg-purple-600", initial: "A" },
    { name: "Francesco Nuzzi", text: "Abbiamo aperto il negozio da poco più di un anno e grazie alla professionalità e disponibilità di Riccardo, il negozio adesso ha un'ottima visibilità sia sulle mappe che su Google.", date: "5 anni fa", avatarColor: "bg-teal-600", initial: "F" },
    { name: "La fenice Sapia", text: "La quattropuntozero ci ha aiutato a risolvere tutte le problematiche riguardanti Google ed i servizi ad esso annessi, portando risultati immediati all'immagine della nostra attività.", date: "4 anni fa", avatarColor: "bg-indigo-500", initial: "L" },
    { name: "Cristiano", text: "È sempre un piacere lavorare con Riccardo, riesce a tirare fuori sempre il meglio da ogni scatto. Coinvolgente e geniale.", date: "6 anni fa", avatarColor: "bg-rose-500", initial: "C" },
    { name: "FILIPPO BOSCAGLI", text: "Grazie mille Riccardo. Professionalità e gentilezza al top. Ci hai aiutato a capire meglio le potenzialità di questo mondo.", date: "5 anni fa", avatarColor: "bg-amber-500", initial: "F" },
  ];

  const totalPages = Math.ceil(reviews.length / 3);

  React.useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, totalPages]);

  const currentReviews = reviews.slice(currentPage * 3, currentPage * 3 + 3);

  return (
    <section className="py-20 bg-gray-900" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 mb-4">Recensioni Verificate Google</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-google">Cosa Dicono di Noi in Valdelsa</h2>
          {/* Screenshot reale della scheda Google */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-800 rounded-2xl p-1 shadow-2xl border border-gray-700 inline-block" style={{ boxShadow: '0 0 40px rgba(250,204,21,0.1)' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_5efdd782-584e-41f8-94c7-665072b1a298/artifacts/ij8eo9rn_chrome_zxPWiVc83A.png"
                alt="SOS Business Profile — 5,0 stelle — 50 recensioni su Google"
                className="rounded-xl w-64 md:w-80"
                data-testid="google-rating-screenshot"
              />
            </div>
          </div>
          <p className="text-gray-400 text-sm">Recensioni reali dalla scheda "SOS Business Profile"</p>
        </div>

        {/* Cards con transizione */}
        <div className="grid md:grid-cols-3 gap-6 mb-8" onMouseEnter={() => setIsAutoPlaying(false)} onMouseLeave={() => setIsAutoPlaying(true)}>
          {currentReviews.map((review, index) => (
            <Card key={`${currentPage}-${index}`} className="bg-gray-800 border-gray-700 transition-all duration-500 animate-in fade-in slide-in-from-bottom-4">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 ${review.avatarColor} rounded-full flex items-center justify-center text-white font-bold text-lg`}>{review.initial}</div>
                  <div>
                    <p className="text-white font-semibold">{review.name}</p>
                    <div className="flex items-center gap-1 text-sm">
                      <span className="text-gray-400">Recensione di</span>
                      <span className="text-blue-400 font-medium">G</span><span className="text-red-400 font-medium">o</span><span className="text-yellow-400 font-medium">o</span><span className="text-blue-400 font-medium">g</span><span className="text-green-400 font-medium">l</span><span className="text-red-400 font-medium">e</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                  <span className="text-gray-500 ml-2">·</span>
                  <span className="text-gray-400 text-sm ml-1">{review.date}</span>
                </div>
                <p className="text-gray-300 italic text-sm leading-relaxed">"{review.text}"</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination dots */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrentPage(i); setIsAutoPlaying(false); setTimeout(() => setIsAutoPlaying(true), 10000); }}
              className={`transition-all duration-300 rounded-full ${i === currentPage ? 'w-8 h-3 bg-[#ff6b35]' : 'w-3 h-3 bg-gray-600 hover:bg-gray-500'}`}
              data-testid={`review-dot-${i}`}
            />
          ))}
        </div>

        <div className="text-center">
          <Button size="lg" variant="outline" className="border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white rounded-full"
            onClick={() => window.open(GMAPS_REVIEWS, '_blank')} data-testid="all-reviews-btn">
            Vedi Tutte le Recensioni su Google <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default function PlusPage() {
  return (
    <>
      <PlusPricingCard />
      <SuspensionAlertSection />
      <SectionDivider />
      <ManifestoSection />
      <ReviewsSection />
      <WhatsAppBookingForm pacchetto="Plus" color="#EF4444" />
      <TShirtBundle pacchetto="Plus" />
      <CrossSellingGift pacchetto="Plus" />
      <CrossSellingProducts pacchetto="Plus" />
      <ContactSection />
    </>
  );
}
